// Field paths of the change log (spec/dashboard.md section 4.1): dotted keys with list indices in brackets,
// `reward.items[1].count`. Values are treated as immutable: `setAt` copies the containers it touches.
import type { Json } from '../api/types';

export type PathSegment = string | number;

export function parsePath(path: string): PathSegment[] {
  const out: PathSegment[] = [];
  if (path === '' || path === '$') return out;
  const re = /([^.[\]]+)|\[(\d+)\]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(path)) !== null) {
    if (m[1] !== undefined) out.push(m[1]);
    else out.push(Number(m[2]));
  }
  return out;
}

export function joinPath(segments: PathSegment[]): string {
  let s = '';
  for (const seg of segments) {
    if (typeof seg === 'number') s += `[${seg}]`;
    else s += s ? `.${seg}` : seg;
  }
  return s;
}

export function getAt(value: unknown, path: string | PathSegment[]): unknown {
  const segments = typeof path === 'string' ? parsePath(path) : path;
  let cur: unknown = value;
  for (const seg of segments) {
    if (cur === null || cur === undefined) return undefined;
    if (typeof seg === 'number') {
      if (!Array.isArray(cur)) return undefined;
      cur = cur[seg];
    } else {
      if (typeof cur !== 'object' || Array.isArray(cur)) return undefined;
      cur = (cur as Record<string, unknown>)[seg];
    }
  }
  return cur;
}

/** A copy of `value` with `path` set to `next` (`undefined` removes an object key or a list element). */
export function setAt<T>(value: T, path: string | PathSegment[], next: unknown): T {
  const segments = typeof path === 'string' ? parsePath(path) : path;
  if (segments.length === 0) return next as T;
  return setSegments(value, segments, 0, next) as T;
}

function setSegments(value: unknown, segments: PathSegment[], index: number, next: unknown): unknown {
  const seg = segments[index]!;
  const last = index === segments.length - 1;
  if (typeof seg === 'number') {
    const list = Array.isArray(value) ? [...value] : [];
    if (last) {
      if (next === undefined) list.splice(seg, 1);
      else list[seg] = next;
    } else {
      list[seg] = setSegments(list[seg], segments, index + 1, next);
    }
    return list;
  }
  const obj: Record<string, unknown> = value && typeof value === 'object' && !Array.isArray(value) ? { ...(value as Record<string, unknown>) } : {};
  if (last) {
    if (next === undefined) delete obj[seg];
    else obj[seg] = next;
  } else {
    obj[seg] = setSegments(obj[seg], segments, index + 1, next);
  }
  return obj;
}

export function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return Number.isNaN(a as number) && Number.isNaN(b as number);
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) {
    const bb = b as unknown[];
    return a.length === bb.length && a.every((v, i) => deepEqual(v, bb[i]));
  }
  const ka = Object.keys(a as object);
  const kb = Object.keys(b as object);
  if (ka.length !== kb.length) return false;
  return ka.every((k) => deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
}

export function clone<T>(value: T): T {
  return value === undefined ? value : (JSON.parse(JSON.stringify(value)) as T);
}

/** Every leaf path of a JSON value (objects and lists descended; empty containers are leaves). */
export function leafPaths(value: Json, prefix: PathSegment[] = [], out: string[] = []): string[] {
  if (value !== null && typeof value === 'object') {
    const entries = Array.isArray(value) ? value.map((v, i) => [i, v] as [PathSegment, Json]) : Object.entries(value);
    if (entries.length === 0) out.push(joinPath(prefix));
    for (const [k, v] of entries) leafPaths(v, [...prefix, k], out);
    return out;
  }
  out.push(joinPath(prefix));
  return out;
}

/** Leaf-level differences between two JSON values: one row per changed leaf (the DiffView's input). */
export function diffLeaves(before: Json | undefined, after: Json | undefined): { path: string; before: unknown; after: unknown }[] {
  const paths = new Set<string>([...leafPaths(before ?? null), ...leafPaths(after ?? null)]);
  // a side that is null (a created or deleted value) contributes the root as its only leaf: the other side's leaves say it all
  if (paths.size > 1 && (before === null || before === undefined || after === null || after === undefined)) paths.delete('');
  const rows: { path: string; before: unknown; after: unknown }[] = [];
  for (const p of Array.from(paths).sort()) {
    const b = getAt(before, p);
    const a = getAt(after, p);
    if (!deepEqual(a, b)) rows.push({ path: p === '' ? '$' : p, before: b, after: a });
  }
  return rows;
}
